// ui/src/components/admin/RedFlagItem.tsx

interface RedFlagItemProps {
  flag: {
    id: string;
    label: string;
    description: string;
    guidance?: string;
    type: 'predefined' | 'custom';
  };
  enabled: boolean;
  onToggle?: (flagId: string, enabled: boolean) => void;
}

export function RedFlagItem({ flag, enabled, onToggle }: RedFlagItemProps) {
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg ${
        enabled ? 'opacity-100' : 'opacity-60'
      }`}
    >
      {/* Toggle switch */}
      {onToggle ? (
        <button
          type="button"
          onClick={() => onToggle(flag.id, !enabled)}
          className={`relative w-10 h-5 rounded-full mt-0.5 flex-shrink-0 transition-colors ${
            enabled ? 'bg-green-600' : 'bg-wolf-600'
          }`}
          aria-pressed={enabled}
          aria-label={`${enabled ? 'Disable' : 'Enable'} ${flag.label}`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
              enabled ? 'left-5' : 'left-0.5'
            }`}
          />
        </button>
      ) : (
        /* Status dot (fallback when no toggle) */
        <div
          className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${
            enabled ? 'bg-green-500' : 'bg-wolf-600'
          }`}
        />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Label + Tag row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-white">{flag.label}</span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              flag.type === 'predefined'
                ? 'bg-wolf-700 text-wolf-300'
                : 'bg-blue-900 text-blue-300'
            }`}
          >
            {flag.type === 'predefined' ? 'Predefined' : 'Custom'}
          </span>
        </div>

        {/* Description */}
        <p className="text-sm text-wolf-300 mt-1">{flag.description}</p>

        {/* Guidance (only for predefined flags) */}
        {flag.guidance && (
          <p className="text-sm text-wolf-400 italic mt-2">{flag.guidance}</p>
        )}
      </div>
    </div>
  );
}
